import { applyDuplicateContentRefParams, type DuplicateContentParams } from '../documents';
import { createFile, getFileBlobDownloadURL } from '../files';
import { createPageComputed, createPageDocument, createPageGroup, createPageLink } from '../pages';
import { createReusableContent } from '../reusableContent';
import {
    assertPageWithDescendants,
    type Revision,
    type RevisionFileBlobMetadata,
    type RevisionPageBase,
    type RevisionPageComputed,
    type RevisionPageDocument,
    type RevisionPageGroup,
    type RevisionPageHideable,
    type RevisionPageLink,
    RevisionPageType,
    type RevisionReusableContent,
} from '../types';
import { randomUUID } from '../utils';
import { getPageDescendantsInRevision } from './mutations';

/**
 * A task created by `duplicateRevision`.
 */
export type DuplicateContentTask = DuplicateContentTaskFile | DuplicateContentTaskDocument;

/**
 * A task for duplicating a file.
 * The blob `blobId` will be created with the content of `originDownloadURL` or `originBlobId`.
 */
export interface DuplicateContentTaskFile {
    type: 'duplicate-file';

    /** ID of the content where the origin blob is */
    originContentId: string;

    /** ID of the blob to create */
    blobId: string;

    /** URL of the file to duplicate */
    originDownloadURL: string;

    /** ID of the blob to duplicate if stored in the bucket */
    originBlobId: string | undefined;

    /** Metadata for the file */
    metadata: RevisionFileBlobMetadata;
}

/**
 * A task for duplicating a document.
 * The document `documentId` will be created with the content of `originDocumentId`.
 */
export interface DuplicateContentTaskDocument {
    type: 'duplicate-document';

    /** ID of the content where the origin document is */
    originContentId: string;

    /** ID of the document to create */
    documentId: string;

    /** ID of the origin document to duplicate */
    originDocumentId: string;

    /** Params to be applied to the duplicated content */
    params?: DuplicateContentParams;
}

export type DuplicateContentOptions = {
    /**
     * If true, will also set meta links to the original pages during the duplication. Defaults to false
     * @default false
     */
    withMetaLinks?: boolean;
};

/**
 * Duplicate the originRevision into the given revision.
 * Returns the modified revision, and the tasks to perform the duplication.
 * This function does not run any tasks.
 */
export function duplicateRevision(param: {
    /** Current revision to duplicate into */
    revision: Revision;

    /** Origin revision to duplicate */
    originRevision: Revision;

    /** ID of the page to start duplicating. If none is provided, the entire tree is duplicated. */
    originPageId?: string;

    /** ID of the page to duplicate into. If none is provided, the entire content is replaced. */
    pageId?: string;

    /** ID of the user duplicating the content */
    byUserId: string;

    /**
     * Base URL to use to generate the download URL of duplicated files.
     */
    baseFileURL: string;

    /**
     * Function to rewrite a legacy downloadURL to use the current legacy bucket.
     */
    rewriteLegacyBucketDownloadURL: (downloadURL: string) => string;

    /**
     * Params to apply to the content template.
     */
    params?: DuplicateContentParams;
    /**
     * Options for the duplication process.
     */
    options?: DuplicateContentOptions;
}): {
    revision: Revision;
    tasks: DuplicateContentTask[];
} {
    const {
        revision,
        originRevision,
        originPageId,
        pageId,
        byUserId,
        baseFileURL,
        rewriteLegacyBucketDownloadURL,
        params,
        options,
    } = param;

    const tasks: DuplicateContentTask[] = [];

    // Insert all files that don't already exist
    Object.entries(originRevision.files).forEach(([fileId, file]) => {
        if (revision[fileId as keyof Revision]) {
            // If the file already exists, we preserve the existing one
            return;
        }

        const blobId = randomUUID();

        revision.files[fileId] = createFile(
            {
                id: fileId,
                name: file.name,
                size: file.size,
                contentType: file.contentType,
                blobId,
                downloadURL: getFileBlobDownloadURL(baseFileURL, revision.contentId, {
                    blobId,
                    name: file.name,
                }),
                dimensions: file.dimensions,
            },
            byUserId
        );

        tasks.push({
            type: 'duplicate-file',
            blobId,
            originContentId: originRevision.contentId,
            originDownloadURL: rewriteLegacyBucketDownloadURL(file.downloadURL),
            originBlobId: file.blobId,
            metadata: {
                name: file.name,
                contentType: file.contentType,
                size: file.size,
            },
        });
    });

    revision.variables = {
        ...revision.variables,
        ...originRevision.variables,
    };

    revision.tags = {
        ...revision.tags,
        ...structuredClone(originRevision.tags),
    };

    revision.reusableContents ??= {};
    Object.entries(originRevision.reusableContents ?? {}).forEach(
        ([reusableContentId, reusableContent]) => {
            const documentId = reusableContent.document ? randomUUID() : undefined;

            revision.reusableContents[reusableContentId] = createDuplicateReusableContent(
                reusableContent,
                {
                    document: documentId,
                }
            );

            if (documentId && reusableContent.document) {
                tasks.push({
                    type: 'duplicate-document',
                    originContentId: originRevision.contentId,
                    documentId,
                    originDocumentId: reusableContent.document,
                    params,
                });
            }
        }
    );

    // If duplicating at the root of the content, we replace the entire tree
    if (!pageId) {
        revision.pages = {};
        revision.rootPages = [];
    }

    // Insert all pages
    const originPages = originPageId
        ? [
              originRevision.pages[originPageId],
              ...getPageDescendantsInRevision(originRevision, originPageId),
          ]
        : Object.values(originRevision.pages);
    const skippedPageIds = new Set(Object.keys(revision.pages));
    const topLevelPages = new Set<string>(originPages.map((page) => page.id));

    originPages.forEach((originPage) => {
        if (skippedPageIds.has(originPage.id)) {
            topLevelPages.delete(originPage.id);
            return;
        }

        if (originPage.type === RevisionPageType.Link) {
            revision.pages[originPage.id] = createDuplicatePageLink(originPage, {
                target: applyDuplicateContentRefParams(originPage.target, params),
            });
            return;
        }

        if (originPage.type === RevisionPageType.Computed) {
            revision.pages[originPage.id] = createDuplicatePageComputed(originPage);
            return;
        }

        // Filter out the descendant that already exist in the rest of the tree.
        const childrenPages = originPage.pages.filter((pageId) => !skippedPageIds.has(pageId));
        childrenPages.forEach((pageId) => {
            topLevelPages.delete(pageId);
        });

        if (originPage.type === RevisionPageType.Group) {
            if (pageId) {
                // Groups can't be inserted in a page, so we insert as a document page instead
                revision.pages[originPage.id] = createDuplicatePageDocument(
                    originRevision,
                    originPage,
                    {
                        pages: childrenPages,
                    }
                );
            } else {
                revision.pages[originPage.id] = createDuplicatePageGroup(originPage, {
                    pages: childrenPages,
                });
            }

            return;
        }

        const documentId = originPage.document ? randomUUID() : undefined;

        revision.pages[originPage.id] = createDuplicatePageDocument(
            originRevision,
            originPage,
            {
                pages: childrenPages,
                document: documentId,
            },
            options
        );

        if (documentId && originPage.document) {
            tasks.push({
                type: 'duplicate-document',
                originContentId: originRevision.contentId,
                documentId,
                originDocumentId: originPage.document,
                params,
            });
        }
    });

    if (pageId) {
        const parentPage = revision.pages[pageId];
        assertPageWithDescendants(parentPage);
        parentPage.pages = [...parentPage.pages, ...topLevelPages];
    } else {
        revision.rootPages = [
            ...revision.rootPages,
            ...(originPageId ? [originPageId] : originRevision.rootPages),
        ];
    }

    return {
        revision,
        tasks,
    };
}

function createDuplicatePageDocument(
    originRevision: Revision,
    originPage: RevisionPageDocument | RevisionPageGroup,
    ext: Partial<RevisionPageDocument>,
    /**
     * Options for the duplication process.
     */
    options?: DuplicateContentOptions
): RevisionPageDocument {
    return createPageDocument({
        ...createDuplicatePageBase(originPage),
        ...createDuplicatePageHideable(originPage),
        // these are optional properties so we don't want to add them as undefined properties.
        // Rather we do not copy them.
        ...(originPage.slug ? { slug: originPage.slug } : {}),
        ...(originPage.type === RevisionPageType.Document
            ? {
                  ...(originPage.description ? { description: originPage.description } : {}),
                  ...(originPage.tagTitle ? { tagTitle: originPage.tagTitle } : {}),
                  ...(originPage.cover ? { cover: originPage.cover } : {}),
                  ...(originPage.layout ? { layout: originPage.layout } : {}),
                  ...(originPage.variables ? { variables: originPage.variables } : {}),
                  ...(originPage.tags ? { tags: originPage.tags } : {}),
              }
            : {}),
        ...(options?.withMetaLinks
            ? {
                  metaLinks: {
                      // When a page is duplicated, we add a meta link to the origin page
                      alternates: [
                          {
                              kind: 'page',
                              page: originPage.id,
                              space: originRevision.contentId,
                          },
                      ],
                  },
              }
            : { metaLinks: undefined }),
        // Extend
        ...ext,
    });
}

function createDuplicatePageGroup(
    originPage: RevisionPageGroup,
    ext: Partial<RevisionPageGroup>
): RevisionPageGroup {
    return createPageGroup({
        ...createDuplicatePageBase(originPage),
        ...createDuplicatePageHideable(originPage),
        ...(originPage.slug ? { slug: originPage.slug } : {}),
        // Extend
        ...ext,
    });
}

function createDuplicatePageLink(
    originPage: RevisionPageLink,
    ext: Partial<RevisionPageLink> = {}
): RevisionPageLink {
    return createPageLink({
        ...createDuplicatePageBase(originPage),
        target: originPage.target,
        // Extend
        ...ext,
    });
}

function createDuplicatePageComputed(
    originPage: RevisionPageComputed,
    ext: Partial<RevisionPageComputed> = {}
): RevisionPageComputed {
    return createPageComputed({
        ...createDuplicatePageBase(originPage),
        computed: originPage.computed,
        // Extend
        ...ext,
    });
}

function createDuplicateReusableContent(
    originReusableContent: RevisionReusableContent,
    ext: Partial<RevisionReusableContent> = {}
): RevisionReusableContent {
    return createReusableContent({
        id: originReusableContent.id,
        title: originReusableContent.title,
        edits: structuredClone(originReusableContent.edits),
        ...(originReusableContent.computed
            ? { computed: structuredClone(originReusableContent.computed) }
            : {}),
        // Extend
        ...ext,
    });
}

function createDuplicatePageBase(originPage: RevisionPageBase): RevisionPageBase {
    return {
        id: originPage.id,
        title: originPage.title,
        linkTitle: originPage.linkTitle,
        edits: {},
        ...(originPage.emoji ? { emoji: originPage.emoji } : {}),
        ...(originPage.icon ? { icon: originPage.icon } : {}),
    };
}

function createDuplicatePageHideable(originPage: RevisionPageHideable): RevisionPageHideable {
    return {
        ...(originPage.hidden ? { hidden: originPage.hidden } : {}),
        ...(originPage.noIndex ? { noIndex: originPage.noIndex } : {}),
        ...(originPage.noRobotsIndex ? { noIndex: originPage.noRobotsIndex } : {}),
    };
}
